/* Onglet « Contrat » du dashboard membre :
 * - contrat(s) d'énergie actuel(s) : prélèvement (consommateur), injection (producteur), les deux (prosumer) ;
 *   affichage, saisie manuelle ou dépôt du PDF (traité par Sun Is Up), copie sur les autres sites du groupe ;
 * - contrat Sun Is Up (début du partage, durée, type de prix, tarif) ;
 * - entreprises : demande d'offre au fournisseur partenaire ; particuliers : intérêt pour un achat groupé ;
 * - prix day-ahead Belgique, avec le prix des contrats du site en option.
 * Les écritures passent par la fonction member-contract (fiche participant, e-mail à info@sunisup.be, tâche CRM). */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { format, parseISO } from 'date-fns';
import { fr, nl, enUS } from 'date-fns/locale';
import { Building2, CheckCircle2, FileText, Loader2, Pencil, ShoppingCart, Upload, Users, Zap } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { DayAheadChart } from './DayAheadChart';
import { CONTRACT_FIELDS, type ContractKind, contractKinds, hasSupplierContract, fixedPriceOf, isBiHourly, variableFormulaOf } from './contractFields';

interface Props {
  participant: any;
  groupParticipants: any[];
  isGroupDashboard: boolean;
  isMixedGroup: boolean;
  userEmail?: string | null;
  onUpdated: () => Promise<void> | void;
}

type FormulaType = 'fixed' | 'variable';
interface ContractForm {
  supplier_name: string;
  formula_type: FormulaType;
  tariff_type: 'mono' | 'bi';
  fixed_price: string;
  fixed_price_hp: string;
  fixed_price_hc: string;
  x_param: string;
  a: string;
  b: string;
  a1: string;
  b1: string;
  a2: string;
  b2: string;
  start_date: string;
  end_date: string;
}

const CARD = 'bg-white border border-[#C9DFE5] rounded-2xl p-5 md:p-6';
const H2 = 'font-sans text-xl leading-tight font-bold text-[#00516F]';
const INPUT = 'w-full h-10 px-3 border border-[#C9DFE5] rounded-[10px] text-sm text-[#0C1C2C] bg-white focus:outline-none focus:ring-2 focus:ring-[#00516F]/30';
const BTN = 'inline-flex items-center justify-center gap-2 h-10 px-4 rounded-[10px] border border-[#C9DFE5] bg-white text-sm font-medium text-[#0C1C2C] hover:border-[#00516F] disabled:opacity-50 transition-colors';
const BTN_PRIMARY = 'inline-flex items-center justify-center gap-2 h-10 px-5 rounded-[10px] bg-[#FCAE0A] hover:bg-[#F0A200] text-sm font-semibold text-[#0C1C2C] disabled:opacity-50 transition-colors';
const DEFAULT_X_PARAMS = ['Belpex', 'Endex 101', 'Endex 303'];

const numberOrNull = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2.5 border-b border-[#E3ECEF] last:border-0">
      <span className="text-sm text-[#51626E]">{label}</span>
      <span className="text-sm font-semibold text-[#0C1C2C] text-right tabular-nums">{children}</span>
    </div>
  );
}

function formFrom(p: any, kind: ContractKind): ContractForm {
  const F = CONTRACT_FIELDS[kind];
  return {
    supplier_name: p?.[F.supplier] ?? '',
    formula_type: p?.[F.type] === 'variable' ? 'variable' : 'fixed',
    tariff_type: p?.[F.tariff] === 'bi' ? 'bi' : 'mono',
    fixed_price: p?.[F.fixed] != null ? String(p[F.fixed]) : '',
    fixed_price_hp: p?.[F.hp] != null ? String(p[F.hp]) : '',
    fixed_price_hc: p?.[F.hc] != null ? String(p[F.hc]) : '',
    x_param: p?.[F.x] ?? '',
    a: p?.[F.a] != null ? String(p[F.a]) : '',
    a1: p?.[F.a1] != null ? String(p[F.a1]) : '',
    b1: p?.[F.b1] != null ? String(p[F.b1]) : '',
    a2: p?.[F.a2] != null ? String(p[F.a2]) : '',
    b2: p?.[F.b2] != null ? String(p[F.b2]) : '',
    b: p?.[F.b] != null ? String(p[F.b]) : '',
    start_date: p?.[F.start] ?? '',
    end_date: p?.[F.end] ?? '',
  };
}

type Tr = (key: string, opts?: Record<string, unknown>) => string;

/** Appel de la fonction member-contract (session du membre), avec des messages d'erreur traduits. */
async function callMemberContract(body: Record<string, unknown>, tc: Tr): Promise<any> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error(tc('errors.session'));
  let res: Response;
  try {
    res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/member-contract`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? '',
      },
      body: JSON.stringify(body),
    });
  } catch {
    throw new Error(tc('errors.unreachable'));
  }
  const payload = await res.json().catch(() => null);
  if (!res.ok || payload?.error) {
    if (res.status === 404) throw new Error(tc('errors.unreachable'));
    if (res.status === 401 || res.status === 403) throw new Error(tc('errors.access'));
    throw new Error(payload?.error ?? `${tc('errors.generic')} ${res.status}`);
  }
  return payload;
}

const readBase64 = (file: File) => new Promise<string>((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
  reader.onerror = () => reject(reader.error);
  reader.readAsDataURL(file);
});

/* ── Carte d'un contrat d'énergie (prélèvement ou injection) ── */
/** « 0,9 × Belpex − 10 €/MWh » : nombres au format de la langue, signe de B intégré. */
function formulaText(a: unknown, x: unknown, b: unknown, lang: string): string {
  const nf = (v: number) => v.toLocaleString(lang === 'en' ? 'en-GB' : `${lang}-BE`, { maximumFractionDigits: 4 });
  const A = a == null || a === '' ? '—' : nf(Number(a));
  const X = (x as string) || 'X';
  if (b == null || b === '') return `${A} × ${X} + — €/MWh`;
  const B = Number(b);
  if (B === 0) return `${A} × ${X} €/MWh`;
  return `${A} × ${X} ${B < 0 ? '−' : '+'} ${nf(Math.abs(B))} €/MWh`;
}

function SupplierContractCard({ kind, site, others, lang, tc, fmtDate, fmtPrice, xParams, onUpdated, tutorialAnchor }: {
  kind: ContractKind; site: any; others: any[]; lang: string; tc: Tr;
  fmtDate: (iso?: string | null) => string; fmtPrice: (v: unknown) => string; xParams: string[];
  onUpdated: () => Promise<void> | void; tutorialAnchor?: string;
}) {
  const F = CONTRACT_FIELDS[kind];
  const k = (key: string, opts?: Record<string, unknown>) => tc(`supplier.${kind}.${key}`, opts);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<ContractForm>(() => formFrom(site, kind));
  const [applyTo, setApplyTo] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [lastUpload, setLastUpload] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditing(false);
    setForm(formFrom(site, kind));
    setApplyTo(new Set());
    let cancelled = false;
    let q = supabase.from('supplier_invoice_uploads').select('created_at, upload_date, file_name').eq('participant_id', site.id);
    q = kind === 'injection' ? q.like('file_name', 'CONTRAT_INJ_%') : q.like('file_name', 'CONTRAT_%');
    q.order('created_at', { ascending: false }).limit(5)
      .then(({ data }: { data: { created_at: string; upload_date: string | null; file_name: string }[] | null }) => {
        if (cancelled) return;
        const rows = (data ?? []).filter(r => kind === 'injection' || !String(r.file_name).startsWith('CONTRAT_INJ_'));
        setLastUpload(rows[0]?.upload_date ?? rows[0]?.created_at ?? null);
      });
    return () => { cancelled = true; };
  }, [site.id, kind]); // eslint-disable-line react-hooks/exhaustive-deps

  const has = hasSupplierContract(site, kind);
  const isVariable = site[F.type] === 'variable';
  const bi = isBiHourly(site, kind);
  const targetIds = [site.id, ...others.filter(p => applyTo.has(p.id)).map(p => p.id)];
  const set = <K extends keyof ContractForm>(key: K, value: ContractForm[K]) => setForm(f => ({ ...f, [key]: value }));

  const formError = (() => {
    if (!form.supplier_name.trim()) return tc('errors.supplier');
    const okPrice = (v: string) => { const p = numberOrNull(v); return p !== null && p > 0 && p < 1000; };
    if (form.formula_type === 'fixed' && form.tariff_type === 'bi') {
      if (!okPrice(form.fixed_price_hp) || !okPrice(form.fixed_price_hc)) return tc('errors.fixedPriceBi');
    } else if (form.formula_type === 'fixed') {
      if (!okPrice(form.fixed_price)) return tc('errors.fixedPrice');
    } else {
      if (!form.x_param) return tc('errors.xParam');
      if (form.tariff_type === 'bi') {
        if ([form.a1, form.b1, form.a2, form.b2].some(v => numberOrNull(v) === null)) return tc('errors.abBi');
      } else if (numberOrNull(form.a) === null || numberOrNull(form.b) === null) return tc('errors.ab');
    }
    if (form.start_date && form.end_date && form.end_date < form.start_date) return tc('errors.dates');
    return null;
  })();

  const save = async () => {
    if (formError) { toast.error(formError); return; }
    setSaving(true);
    try {
      await callMemberContract({
        action: 'save_supplier_contract',
        kind,
        lang,
        participantIds: targetIds,
        contract: {
          supplier_name: form.supplier_name.trim(),
          formula_type: form.formula_type,
          tariff_type: form.tariff_type,
          fixed_price: form.formula_type === 'fixed' && form.tariff_type === 'mono' ? numberOrNull(form.fixed_price) : null,
          fixed_price_hp: form.formula_type === 'fixed' && form.tariff_type === 'bi' ? numberOrNull(form.fixed_price_hp) : null,
          fixed_price_hc: form.formula_type === 'fixed' && form.tariff_type === 'bi' ? numberOrNull(form.fixed_price_hc) : null,
          x_param: form.formula_type === 'variable' ? form.x_param : null,
          a: form.formula_type === 'variable' && form.tariff_type === 'mono' ? numberOrNull(form.a) : null,
          a1: form.formula_type === 'variable' && form.tariff_type === 'bi' ? numberOrNull(form.a1) : null,
          b1: form.formula_type === 'variable' && form.tariff_type === 'bi' ? numberOrNull(form.b1) : null,
          a2: form.formula_type === 'variable' && form.tariff_type === 'bi' ? numberOrNull(form.a2) : null,
          b2: form.formula_type === 'variable' && form.tariff_type === 'bi' ? numberOrNull(form.b2) : null,
          b: form.formula_type === 'variable' && form.tariff_type === 'mono' ? numberOrNull(form.b) : null,
          start_date: form.start_date || null,
          end_date: form.end_date || null,
        },
      }, tc);
      toast.success(targetIds.length > 1 ? tc('savedMany', { count: targetIds.length }) : tc('saved'));
      setEditing(false);
      await onUpdated();
    } catch (err) {
      toast.error(`${tc('errors.generic')} : ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSaving(false);
    }
  };

  const upload = async (file: File | undefined) => {
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') { toast.error(tc('errors.pdf')); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error(tc('errors.size')); return; }
    setUploading(true);
    try {
      await callMemberContract({
        action: 'upload_supplier_contract', kind, lang, participantIds: targetIds, fileName: file.name, fileContent: await readBase64(file),
      }, tc);
      toast.success(tc('uploaded'));
      setLastUpload(new Date().toISOString());
    } catch (err) {
      toast.error(`${tc('errors.generic')} : ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className={CARD} aria-labelledby={`contract-${kind}-title`} data-tutorial={tutorialAnchor}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <h2 id={`contract-${kind}-title`} className={H2}>{k('title')}</h2>
          <p className="text-sm text-[#51626E] mt-1">{k('subtitle')}</p>
        </div>
        {has && !editing && (
          <button onClick={() => setEditing(true)} className={BTN}><Pencil className="w-4 h-4" />{tc('supplier.edit')}</button>
        )}
      </div>

      {!editing && has && (
        <div>
          <Row label={tc('supplier.name')}>{site[F.supplier] || '—'}</Row>
          <Row label={tc('supplier.type')}>{site[F.type] ? tc(`supplier.${isVariable ? 'variable' : 'fixed'}${bi ? 'Bi' : 'Mono'}`) : '—'}</Row>
          {isVariable && bi ? (
            <>
              <Row label={tc('supplier.formulaHp')}>{formulaText(site[F.a1], site[F.x], site[F.b1], lang)}</Row>
              <Row label={tc('supplier.formulaHc')}>{formulaText(site[F.a2], site[F.x], site[F.b2], lang)}</Row>
            </>
          ) : isVariable ? (
            <Row label={tc('supplier.formula')}>
              {site[F.a] != null || site[F.b] != null ? formulaText(site[F.a], site[F.x], site[F.b], lang) : '—'}
            </Row>
          ) : bi ? (
            <>
              <Row label={tc('supplier.priceHp')}>{fmtPrice(site[F.hp])}</Row>
              <Row label={tc('supplier.priceHc')}>{fmtPrice(site[F.hc])}</Row>
            </>
          ) : (
            <Row label={tc('supplier.fixedPrice')}>{fmtPrice(site[F.fixed])}</Row>
          )}
          {bi && !isVariable && <p className="mt-2 text-xs text-[#51626E]">{tc('supplier.weighted', { price: fmtPrice(fixedPriceOf(site, kind)) })}</p>}
          {bi && isVariable && (() => { const v = variableFormulaOf(site, kind); return (
            <p className="mt-2 text-xs text-[#51626E]">{tc('supplier.weightedFormula', { formula: formulaText(v.a, site[F.x], v.b, lang) })}</p>
          ); })()}
          <Row label={tc('supplier.start')}>{fmtDate(site[F.start])}</Row>
          <Row label={tc('supplier.end')}>{fmtDate(site[F.end])}</Row>
          <p className="mt-2 text-xs text-[#8A9AA5]">{tc('supplier.exclTaxes')}</p>
        </div>
      )}

      {!editing && !has && (
        <div className="rounded-xl border border-dashed border-[#C9DFE5] bg-[#F7FAFB] p-5 text-center">
          <FileText className="w-8 h-8 mx-auto text-[#8A9AA5]" />
          <p className="mt-2 text-sm font-semibold text-[#0C1C2C]">{k('emptyTitle')}</p>
          <p className="mt-1 text-sm text-[#51626E]">{tc('supplier.emptyText')}</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 text-left">
            <div className="rounded-xl bg-white border border-[#C9DFE5] p-4 flex flex-col gap-3">
              <div>
                <p className="text-sm font-semibold text-[#0C1C2C]">1. {tc('supplier.optionPdfTitle')}</p>
                <p className="mt-1 text-xs text-[#51626E] leading-relaxed">{tc('supplier.optionPdfText')}</p>
              </div>
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className={`${BTN_PRIMARY} mt-auto`}>
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}{tc('supplier.upload')}
              </button>
            </div>
            <div className="rounded-xl bg-white border border-[#C9DFE5] p-4 flex flex-col gap-3">
              <div>
                <p className="text-sm font-semibold text-[#0C1C2C]">2. {tc('supplier.optionManualTitle')}</p>
                <p className="mt-1 text-xs text-[#51626E] leading-relaxed">{tc('supplier.optionManualText')}</p>
              </div>
              <button onClick={() => setEditing(true)} className={`${BTN} mt-auto`}><Pencil className="w-4 h-4" />{tc('supplier.manual')}</button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="space-y-3">
          <label className="block">
            <span className="block text-xs font-medium text-[#51626E] mb-1">{tc('supplier.name')} *</span>
            <input value={form.supplier_name} onChange={e => set('supplier_name', e.target.value)} placeholder="Engie, Luminus, TotalEnergies…" className={INPUT} />
          </label>
          <div>
            <span className="block text-xs font-medium text-[#51626E] mb-1">{tc('supplier.type')} *</span>
            <div className="inline-flex rounded-[10px] border border-[#C9DFE5] overflow-hidden">
              {(['fixed', 'variable'] as FormulaType[]).map(v => (
                <button key={v} type="button" onClick={() => set('formula_type', v)} aria-pressed={form.formula_type === v}
                  className={`px-4 h-9 text-sm font-medium transition-colors ${form.formula_type === v ? 'bg-[#00516F] text-white' : 'bg-white text-[#51626E] hover:bg-[#F2F2F3]'}`}>
                  {tc(v)}
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="block text-xs font-medium text-[#51626E] mb-1">{tc('supplier.tariff')} *</span>
            <div className="inline-flex rounded-[10px] border border-[#C9DFE5] overflow-hidden">
              {(['mono', 'bi'] as const).map(v => (
                <button key={v} type="button" onClick={() => set('tariff_type', v)} aria-pressed={form.tariff_type === v}
                  className={`px-4 h-9 text-sm font-medium transition-colors ${form.tariff_type === v ? 'bg-[#00516F] text-white' : 'bg-white text-[#51626E] hover:bg-[#F2F2F3]'}`}>
                  {tc(`supplier.${v}`)}
                </button>
              ))}
            </div>
          </div>
          {form.formula_type === 'fixed' ? (
            form.tariff_type === 'bi' ? (
              <div className="grid grid-cols-2 gap-2">
                <label className="block">
                  <span className="block text-xs font-medium text-[#51626E] mb-1">{tc('supplier.priceHp')} (€/MWh) *</span>
                  <input type="number" step="any" min="0" value={form.fixed_price_hp} onChange={e => set('fixed_price_hp', e.target.value)} placeholder={tc('supplier.examplePrice')} className={INPUT} />
                </label>
                <label className="block">
                  <span className="block text-xs font-medium text-[#51626E] mb-1">{tc('supplier.priceHc')} (€/MWh) *</span>
                  <input type="number" step="any" min="0" value={form.fixed_price_hc} onChange={e => set('fixed_price_hc', e.target.value)} placeholder={tc('supplier.examplePrice')} className={INPUT} />
                </label>
              </div>
            ) : (
              <label className="block">
                <span className="block text-xs font-medium text-[#51626E] mb-1">{tc('supplier.fixedPrice')} (€/MWh) *</span>
                <input type="number" step="any" min="0" value={form.fixed_price} onChange={e => set('fixed_price', e.target.value)} placeholder={tc('supplier.examplePrice')} className={INPUT} />
              </label>
            )
          ) : (
            <div className="space-y-2">
              <label className="block">
                <span className="block text-xs font-medium text-[#51626E] mb-1">X ({tc('supplier.index')}) *</span>
                <select value={form.x_param} onChange={e => set('x_param', e.target.value)} className={INPUT}>
                  <option value="">—</option>
                  {Array.from(new Set([...xParams, ...(form.x_param ? [form.x_param] : [])])).map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </label>
              {(form.tariff_type === 'bi'
                ? [{ label: tc('supplier.formulaHp'), a: 'a1', b: 'b1', n: '1' }, { label: tc('supplier.formulaHc'), a: 'a2', b: 'b2', n: '2' }] as const
                : [{ label: tc('supplier.formula'), a: 'a', b: 'b', n: '' }] as const
              ).map(row => (
                <div key={row.a}>
                  <span className="block text-xs font-medium text-[#51626E] mb-1">{row.label} : A{row.n} × X + B{row.n} (€/MWh) *</span>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="block text-[11px] text-[#8A9AA5] mb-1">A{row.n}</span>
                      <input type="number" step="any" value={form[row.a]} onChange={e => set(row.a, e.target.value)} placeholder="1,1" className={INPUT} />
                    </label>
                    <label className="block">
                      <span className="block text-[11px] text-[#8A9AA5] mb-1">B{row.n}</span>
                      <input type="number" step="any" value={form[row.b]} onChange={e => set(row.b, e.target.value)} placeholder="15" className={INPUT} />
                    </label>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-[#8A9AA5]">{tc('supplier.exclTaxes')}</p>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="block text-xs font-medium text-[#51626E] mb-1">{tc('supplier.start')}</span>
              <input type="date" value={form.start_date} onChange={e => set('start_date', e.target.value)} className={INPUT} />
            </label>
            <label className="block">
              <span className="block text-xs font-medium text-[#51626E] mb-1">{tc('supplier.end')}</span>
              <input type="date" value={form.end_date} min={form.start_date || undefined} onChange={e => set('end_date', e.target.value)} className={INPUT} />
            </label>
          </div>
        </div>
      )}

      {/* Copie sur les autres sites du groupe (saisie et dépôt du PDF) */}
      {(editing || !has) && others.length > 0 && (
        <div className="mt-4 rounded-xl bg-[#F7FAFB] border border-[#E3ECEF] p-3">
          <div className="flex items-center justify-between gap-2 mb-2">
            <span className="text-xs font-semibold text-[#0C1C2C]">{tc('applyTo')}</span>
            <button type="button" className="text-xs font-medium text-[#00516F] hover:underline"
              onClick={() => setApplyTo(applyTo.size === others.length ? new Set() : new Set(others.map(p => p.id)))}>
              {applyTo.size === others.length ? tc('none') : tc('all')}
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {others.map(p => (
              <label key={p.id} className="flex items-center gap-2 text-sm text-[#0C1C2C] cursor-pointer">
                <input type="checkbox" className="accent-[#00516F]" checked={applyTo.has(p.id)}
                  onChange={e => setApplyTo(prev => { const n = new Set(prev); if (e.target.checked) n.add(p.id); else n.delete(p.id); return n; })} />
                <span className="truncate">{p.name}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {editing && (
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          <button onClick={() => { setEditing(false); setForm(formFrom(site, kind)); }} className={BTN}>{tc('cancel')}</button>
          <button onClick={save} disabled={saving || !!formError} title={formError ?? undefined} className={BTN_PRIMARY}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {targetIds.length > 1 ? tc('saveMany', { count: targetIds.length }) : tc('save')}
          </button>
        </div>
      )}

      <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={e => upload(e.target.files?.[0])} />
      <div className="mt-4 flex flex-wrap items-center justify-between gap-2 text-xs text-[#51626E]">
        <span>{lastUpload ? tc('supplier.lastUpload', { date: fmtDate(lastUpload.slice(0, 10)) }) : tc('supplier.noUpload')}</span>
        {(has || editing) && (
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 font-medium text-[#00516F] hover:underline disabled:opacity-50">
            {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}{tc('supplier.uploadDoc')}
          </button>
        )}
      </div>
    </section>
  );
}

export function ContractTab({ participant, groupParticipants, isGroupDashboard, isMixedGroup, userEmail, onUpdated }: Props) {
  const { t, i18n } = useTranslation();
  const tc: Tr = (key, opts) => t(`memberContract.${key}`, opts) as string;
  const locale = i18n.language === 'nl' ? nl : i18n.language === 'en' ? enUS : fr;
  const lang = ['fr', 'nl', 'en'].includes(i18n.language) ? i18n.language : 'fr';
  const numLocale = i18n.language === 'en' ? 'en-GB' : i18n.language === 'nl' ? 'nl-BE' : 'fr-BE';
  const fmtDate = (iso?: string | null) => {
    if (!iso) return '—';
    try { return format(parseISO(iso), 'd MMMM yyyy', { locale }); } catch { return iso; }
  };
  const fmtPrice = (v: unknown) => {
    const n = numberOrNull(v);
    return n === null ? '—' : `${n.toLocaleString(numLocale, { maximumFractionDigits: 2 })} €/MWh`;
  };

  const sites: any[] = useMemo(
    () => (isGroupDashboard && groupParticipants.length > 0 ? groupParticipants : [participant]),
    [isGroupDashboard, groupParticipants, participant],
  );
  const [selectedId, setSelectedId] = useState<string>(participant.id);
  const selected = sites.find(p => p.id === selectedId) ?? sites[0] ?? participant;
  const kinds = contractKinds(selected.type);
  /* Copie vers les autres sites : seulement ceux qui ont le même type de contrat. */
  const othersFor = (kind: ContractKind) => sites.filter(p => p.id !== selected.id && contractKinds(p.type).includes(kind));

  const [xParams, setXParams] = useState<string[]>(DEFAULT_X_PARAMS);
  useEffect(() => {
    supabase.from('indexation_parameters').select('param_name').then(({ data }: { data: { param_name: string }[] | null }) => {
      const names = Array.from(new Set((data ?? []).map(r => r.param_name).filter(Boolean))).sort();
      if (names.length > 0) setXParams(names);
    });
  }, []);

  /* ── Contrat Sun Is Up ── */
  const members: any[] = isGroupDashboard ? groupParticipants : [participant];
  const prosumer = members.find(p => p.type === 'prosumer');
  const showBoth = isMixedGroup || selected.type === 'prosumer';
  const buyPrice = selected.type === 'prosumer'
    ? (selected.prosumer_buy_price ?? selected.shared_energy_price)
    : isMixedGroup
      ? (prosumer?.prosumer_buy_price ?? members.find(p => p.type === 'consumer')?.shared_energy_price ?? null)
      : selected.shared_energy_price;
  const sellPrice = selected.type === 'prosumer'
    ? (selected.prosumer_sell_price ?? selected.shared_energy_price)
    : isMixedGroup
      ? (prosumer?.prosumer_sell_price ?? members.find(p => p.type === 'producer')?.shared_energy_price ?? null)
      : selected.shared_energy_price;
  const sharedVariable = selected.shared_energy_formula_type === 'variable' && selected.shared_energy_a && selected.shared_energy_x_param;

  /* ── Offre partenaire (entreprises) / intérêt pour un achat groupé (particuliers) ── */
  const isCompany = members.some(p => !!p.company_number);
  const offerKind = isCompany ? 'partner_offer' : 'group_purchase';
  const allKinds = new Set(members.flatMap(p => contractKinds(p.type)));
  // Pas d'offre pour un producteur seul : seulement si au moins un site prélève (consommateur ou prosumer).
  const showOffer = allKinds.has('offtake');
  const offerTextKey = allKinds.has('injection') ? 'offer.textBoth' : 'offer.text';
  const sentKey = `siu-${offerKind}-${participant.id}`;
  const [sentAt, setSentAt] = useState<string | null>(() => { try { return localStorage.getItem(sentKey); } catch { return null; } });
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState('');

  const sendOffer = async () => {
    setSending(true);
    try {
      const res = await callMemberContract({
        action: offerKind, lang, participantIds: members.map(p => p.id), message: isCompany ? note.trim() : undefined,
      }, tc);
      const now = new Date().toISOString();
      try { localStorage.setItem(sentKey, now); } catch { /* stockage indisponible */ }
      setSentAt(now);
      setConfirming(false);
      setNote('');
      toast.success(isCompany ? tc('offer.sent') : tc('purchase.sent', { email: res?.confirmationTo ?? participant.email ?? userEmail ?? '' }));
    } catch (err) {
      toast.error(`${tc('errors.generic')} : ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      {isGroupDashboard && sites.length > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-[#51626E]">{tc('perSite')}</p>
          <label className="flex items-center gap-2 text-sm text-[#51626E]">
            <Users className="w-4 h-4" />
            <select value={selected.id} onChange={e => setSelectedId(e.target.value)}
              className="h-9 px-3 border border-[#C9DFE5] rounded-[10px] text-sm font-semibold text-[#0C1C2C] bg-white focus:outline-none focus:ring-2 focus:ring-[#00516F]/30">
              {sites.map(p => <option key={p.id} value={p.id}>{p.name}{p.ean_code ? ` · ${p.ean_code}` : ''}</option>)}
            </select>
          </label>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2 items-start">
        {/* Contrat(s) d'énergie actuel(s) */}
        {kinds.map((kind, i) => (
          <SupplierContractCard key={`${selected.id}-${kind}`} kind={kind} site={selected} others={othersFor(kind)} lang={lang} tc={tc}
            fmtDate={fmtDate} fmtPrice={fmtPrice} xParams={xParams} onUpdated={onUpdated}
            tutorialAnchor={i === 0 ? 'tutorial-contract-supplier' : undefined} />
        ))}

        {/* Contrat Sun Is Up */}
        <section className={CARD} aria-labelledby="siu-contract-title" data-tutorial="tutorial-contract-siu">
          <div className="mb-4">
            <h2 id="siu-contract-title" className={H2}>{tc('siu.title')}</h2>
            <p className="text-sm text-[#51626E] mt-1">{tc('siu.subtitle')}</p>
          </div>
          <Row label={tc('siu.start')}>{fmtDate(selected.entry_date)}</Row>
          <Row label={tc('siu.duration')}>{tc('siu.indefinite')}</Row>
          <Row label={tc('siu.priceType')}>{tc(sharedVariable ? 'variable' : 'fixed')}</Row>
          {sharedVariable ? (
            <Row label={tc('siu.price')}>{`${selected.shared_energy_a} × ${selected.shared_energy_x_param}`}</Row>
          ) : showBoth ? (
            <>
              <Row label={tc('siu.buyPrice')}>{fmtPrice(buyPrice)}</Row>
              <Row label={tc('siu.sellPrice')}>{fmtPrice(sellPrice)}</Row>
            </>
          ) : selected.type === 'producer' ? (
            <Row label={tc('siu.sellPrice')}>{fmtPrice(sellPrice)}</Row>
          ) : (
            <Row label={tc('siu.buyPrice')}>{fmtPrice(buyPrice)}</Row>
          )}
          <p className="mt-3 text-xs text-[#8A9AA5]">{tc('siu.note')}</p>
        </section>
      </div>

      {/* Offre fournisseur partenaire / intérêt pour un achat groupé */}
      {showOffer && <section className={`${CARD} flex flex-wrap items-center gap-5`} data-tutorial="tutorial-contract-offer">
        <span className="w-12 h-12 rounded-xl bg-[#FFF8E6] flex items-center justify-center text-[#B35F0F] shrink-0">
          {isCompany ? <Building2 className="w-6 h-6" /> : <ShoppingCart className="w-6 h-6" />}
        </span>
        <div className="flex-[1_1_320px] min-w-0">
          <h2 className={H2}>{isCompany ? tc('offer.title') : tc('purchase.title')}</h2>
          <p className="text-sm text-[#51626E] mt-1">{isCompany ? tc(offerTextKey) : tc('purchase.text')}</p>
          {sentAt && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-[#00516F]">
              <CheckCircle2 className="w-3.5 h-3.5" />{tc(isCompany ? 'offer.done' : 'purchase.done', { date: fmtDate(sentAt.slice(0, 10)) })}
            </p>
          )}
        </div>
        {!confirming ? (
          <button onClick={() => setConfirming(true)} className={BTN_PRIMARY}>
            {isCompany ? <Building2 className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
            {isCompany ? tc('offer.button') : tc('purchase.button')}
          </button>
        ) : (
          <div className="w-full flex flex-col gap-3 rounded-xl bg-[#F7FAFB] border border-[#E3ECEF] p-4">
            <p className="text-sm text-[#0C1C2C]">
              {isCompany ? tc('offer.confirm') : tc('purchase.confirm', { email: participant.email || userEmail || '' })}
            </p>
            {isCompany && (
              <textarea rows={3} value={note} onChange={e => setNote(e.target.value)} placeholder={tc('offer.notePlaceholder')}
                className="w-full px-3 py-2 border border-[#C9DFE5] rounded-[10px] text-sm text-[#0C1C2C] bg-white resize-none focus:outline-none focus:ring-2 focus:ring-[#00516F]/30" />
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button onClick={() => setConfirming(false)} className={BTN}>{tc('cancel')}</button>
              <button onClick={sendOffer} disabled={sending} className={BTN_PRIMARY}>
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}{tc('confirmSend')}
              </button>
            </div>
          </div>
        )}
      </section>}

      {/* Prix day-ahead Belgique (ENTSO-E), avec le prix des contrats du site en option */}
      <div data-tutorial="tutorial-contract-market">
        <DayAheadChart site={selected} kinds={kinds} producerView={selected.type === 'producer' || selected.type === 'prosumer'} />
      </div>
    </div>
  );
}

export default ContractTab;
